import { useState, useEffect, useMemo } from 'react';
import { RefreshCw, FileText, CheckCircle2, Printer } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import Skeleton from './Skeleton';
import { EmptyStateError } from './EmptyState';
import { PieChart } from './Charts';

interface UnpreparedOrderLine {
  order_number: string;
  order_id: number;
  article_name: string;
  sku: string;
  quantity: number;
  price: number;
  notes: string;
  days_since_order: number;
  created_at: string;
}

interface BOMItem {
  cod_pieza: string;
  nombre_pieza: string;
  cantidad_necesaria: number;
  stock_acabado: boolean;
  stock_taller: boolean;
  pedir_fundicion: boolean;
  cantidad_fundicion: number;
  acabados_necesarios?: Array<{ acabado: string; cantidad: number }>;
}

export default function Montaje() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState<UnpreparedOrderLine[]>([]);
  const [bomItems, setBomItems] = useState<BOMItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingBOM, setLoadingBOM] = useState(false);
  const [isSendingToAmphora, setIsSendingToAmphora] = useState<Record<string, boolean>>({});

  const loadUnpreparedOrders = async () => {
    try {
      setRefreshing(true);
      const response = await api.get('/shopify/unprepared-orders');
      setOrders(response.data);
    } catch (err: any) {
      console.error('Error cargando pedidos sin preparar:', err);
      showError(err.response?.data?.error || 'Error al cargar pedidos sin preparar');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadBOM = async () => {
    try {
      setLoadingBOM(true);
      const response = await api.get('/shopify/unprepared-orders/bom');
      setBomItems(response.data);
    } catch (err: any) {
      console.error('Error cargando BOM:', err);
      showError(err.response?.data?.error || 'Error al cargar BOM necesario');
    } finally {
      setLoadingBOM(false);
    }
  };

  const handleRefresh = async () => {
    await loadUnpreparedOrders();
    await loadBOM();
    showSuccess('Pedidos actualizados correctamente');
  };

  const handlePrintPDF = async () => {
    try {
      // Enviar el BOM ya calculado para evitar recalcularlo en el servidor
      const response = await api.post('/shopify/unprepared-orders/pdf',
        { bomItems: bomItems },
        { responseType: 'blob' }
      );

      if (!response.data || response.data.size === 0) {
        showError('El PDF generado está vacío. Por favor, reintenta.');
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'preparacion-pedidos.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      showSuccess('PDF generado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar PDF');
    }
  };

  const handlePrintLabelsPDF = async () => {
    try {
      showSuccess('Generando etiquetas, por favor espera...');
      const response = await api.get('/shopify/unprepared-orders/labels/pdf', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Abrir PDF en nueva ventana para imprimir
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        // Si no se puede abrir, descargar
        const link = document.createElement('a');
        link.href = url;
        link.download = `etiquetas-montaje-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      showSuccess('PDF de etiquetas generado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar etiquetas PDF');
    }
  };

  const handleSendToAmphora = async (order: UnpreparedOrderLine) => {
    const rowKey = `${order.order_id}-${order.sku}`;
    try {
      setIsSendingToAmphora(prev => ({ ...prev, [rowKey]: true }));
      
      // 1. Fetch available 3PL shipments
      const resEnvios = await api.get('/3pl/envios');
      const envios: any[] = resEnvios.data || [];
      
      // 2. Find the latest pending shipment (FASE_MONTAJE)
      const pendingShipments = envios.filter(e => e.estado === 'FASE_MONTAJE');
      if (pendingShipments.length === 0) {
        showError('No hay ningún envío a Amphora en estado "Fase de Montaje". Crea uno primero en la sección 3PL.');
        return;
      }
      
      // We take the first one (most recent pending)
      const pendingShipment = pendingShipments[0];
      
      // 3. Send the line to that shipment
      await api.post(`/3pl/envios/${pendingShipment.id}/lines`, {
        sku: order.sku || 'SIN-SKU',
        nombre_producto: order.article_name,
        cantidad: order.quantity
      });
      
      showSuccess(`Añadido ${order.quantity}x ${order.article_name} al envío ${pendingShipment.codigo}`);
      
    } catch (err: any) {
      console.error('Error al enviar a Amphora:', err);
      showError(err.response?.data?.error || 'Error al enviar artículo a Amphora');
    } finally {
      setIsSendingToAmphora(prev => ({ ...prev, [rowKey]: false }));
    }
  };

  useEffect(() => {
    loadUnpreparedOrders();
    loadBOM();
  }, []);

  // Calcular estadísticas para los gráficos
  const stats = useMemo(() => {
    if (orders.length === 0) {
      return {
        totalPedidos: 0,
        totalArticulos: 0,
        pedidosMas3Dias: 0,
        pedidos3DiasOMenos: 0,
        articulosPorTipo: {
          bracelet: 0,
          pendant: 0,
          ring: 0,
          necklace: 0,
          otros: 0
        }
      };
    }

    // Filtrar artículos con precio >= $50 (excluir servicios)
    const articulosFiltrados = orders.filter(o => o.price >= 50);

    // Total de pedidos únicos (solo de artículos con precio >= $50)
    const pedidosUnicos = new Set(articulosFiltrados.map(o => o.order_id));
    const totalPedidos = pedidosUnicos.size;

    // Total de artículos (solo con precio >= $50)
    const totalArticulos = articulosFiltrados.reduce((sum, o) => sum + o.quantity, 0);

    // Agrupar pedidos por order_id y obtener el máximo days_since_order de cada pedido
    // Solo considerar pedidos que tengan al menos un artículo con precio >= $50
    const pedidosPorDias: { [key: number]: number } = {};
    articulosFiltrados.forEach(order => {
      if (!pedidosPorDias[order.order_id] || pedidosPorDias[order.order_id] < order.days_since_order) {
        pedidosPorDias[order.order_id] = order.days_since_order;
      }
    });

    // Contar pedidos con más de 3 días y 3 días o menos
    let pedidosMas3Dias = 0;
    let pedidos3DiasOMenos = 0;
    Object.values(pedidosPorDias).forEach(days => {
      if (days > 3) {
        pedidosMas3Dias++;
      } else {
        pedidos3DiasOMenos++;
      }
    });

    // Distribución por tipo de artículo (solo artículos con precio >= $50)
    const articulosPorTipo = {
      bracelet: 0,
      pendant: 0,
      ring: 0,
      necklace: 0,
      otros: 0
    };

    articulosFiltrados.forEach(order => {
      const nombreLower = order.article_name.toLowerCase();
      if (nombreLower.includes('bracelet')) {
        articulosPorTipo.bracelet += order.quantity;
      } else if (nombreLower.includes('pendant')) {
        articulosPorTipo.pendant += order.quantity;
      } else if (nombreLower.includes('ring')) {
        articulosPorTipo.ring += order.quantity;
      } else if (nombreLower.includes('necklace')) {
        articulosPorTipo.necklace += order.quantity;
      } else {
        articulosPorTipo.otros += order.quantity;
      }
    });

    return {
      totalPedidos,
      totalArticulos,
      pedidosMas3Dias,
      pedidos3DiasOMenos,
      articulosPorTipo
    };
  }, [orders]);

  return (
    <div className="tab-content">
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3>Montaje</h3>
        </div>

        <>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? <><RefreshCw size={16} className="spinning" /> Actualizando...</> : <><RefreshCw size={16} /> Actualizar</>}
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrintPDF}
              disabled={loading || orders.length === 0}
            >
              <FileText size={16} /> Imprimir PDF
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrintLabelsPDF}
              disabled={loading || orders.length === 0}
            >
              <Printer size={16} /> Imprimir Etiquetas Zebra
            </button>
          </div>

          {/* Gráficos de estadísticas */}
          {!loading && orders.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {/* Resumen total */}
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  Resumen General
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                      {stats.totalPedidos}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      Pedidos no preparados
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--interactive)' }}>
                      {stats.totalArticulos}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      Artículos totales
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de retraso de pedidos */}
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  Retraso de Pedidos
                </h4>
                {stats.pedidosMas3Dias + stats.pedidos3DiasOMenos > 0 ? (
                  <PieChart
                    data={[
                      {
                        label: `Más de 3 días (${Math.round((stats.pedidosMas3Dias / (stats.pedidosMas3Dias + stats.pedidos3DiasOMenos)) * 100)}%)`,
                        value: stats.pedidosMas3Dias,
                        color: '#d82c0d' // error
                      },
                      {
                        label: `3 días o menos (${Math.round((stats.pedidos3DiasOMenos / (stats.pedidosMas3Dias + stats.pedidos3DiasOMenos)) * 100)}%)`,
                        value: stats.pedidos3DiasOMenos,
                        color: '#008060' // accent
                      }
                    ]}
                    size={180}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No hay datos
                  </div>
                )}
              </div>

              {/* Gráfico de distribución por tipo de artículo */}
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  Distribución por Tipo
                </h4>
                {stats.totalArticulos > 0 ? (
                  <PieChart
                    data={[
                      {
                        label: `Pulseras (${Math.round((stats.articulosPorTipo.bracelet / stats.totalArticulos) * 100)}%)`,
                        value: stats.articulosPorTipo.bracelet,
                        color: '#008060' // accent
                      },
                      {
                        label: `Colgantes (${Math.round((stats.articulosPorTipo.pendant / stats.totalArticulos) * 100)}%)`,
                        value: stats.articulosPorTipo.pendant,
                        color: '#f49342' // warning
                      },
                      {
                        label: `Anillos (${Math.round((stats.articulosPorTipo.ring / stats.totalArticulos) * 100)}%)`,
                        value: stats.articulosPorTipo.ring,
                        color: '#2c6ecb' // interactive
                      },
                      {
                        label: `Collares (${Math.round((stats.articulosPorTipo.necklace / stats.totalArticulos) * 100)}%)`,
                        value: stats.articulosPorTipo.necklace,
                        color: '#5c6ac4'
                      },
                      ...(stats.articulosPorTipo.otros > 0 ? [{
                        label: `Otros (${Math.round((stats.articulosPorTipo.otros / stats.totalArticulos) * 100)}%)`,
                        value: stats.articulosPorTipo.otros,
                        color: '#47c1bf'
                      }] : [])
                    ]}
                    size={180}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No hay datos
                  </div>
                )}
              </div>
            </div>
          )}

          {loading && !orders.length ? (
            <div className="table-container">
              <Skeleton height="400px" variant="rounded" />
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <p>¡Todo al día! No hay pedidos sin preparar.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nº Pedido</th>
                    <th>Nombre del Artículo</th>
                    <th>Cantidad</th>
                    <th>Notas</th>
                    <th>Días desde Pedido</th>
                    <th style={{ textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => {
                    // Calcular el índice del grupo de pedido para alternar colores
                    // Igual que en el PDF: grupos impares tienen fondo gris
                    let orderGroupIndex = 0;

                    for (let i = 0; i <= index; i++) {
                      const isNewOrder = i === 0 || orders[i - 1].order_number !== orders[i].order_number;
                      if (isNewOrder) {
                        orderGroupIndex++;
                      }
                      if (i === index) {
                        break;
                      }
                    }

                    // Aplicar fondo gris alternado (igual que en PDF: grupos impares = gris)
                    const backgroundColor = orderGroupIndex % 2 === 1 ? '#e8e8e8' : 'transparent';

                    return (
                      <tr
                        key={`${order.order_id}-${index}`}
                        style={{
                          backgroundColor: backgroundColor,
                        }}
                      >
                        <td>
                          <strong>{order.order_number}</strong>
                        </td>
                        <td>{order.article_name}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{order.sku || '-'}</td>
                        <td>{order.quantity}</td>
                        <td style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                          {order.notes || 'Sin notas'}
                        </td>
                        <td>
                          <span
                            style={{
                              color:
                                order.days_since_order >= 3
                                  ? 'var(--error)'
                                  : order.days_since_order >= 1
                                    ? 'var(--warning)'
                                    : 'var(--text-primary)',
                              fontWeight: order.days_since_order >= 3 ? 'bold' : 'normal',
                            }}
                          >
                            {order.days_since_order} {order.days_since_order === 1 ? 'día' : 'días'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ 
                              backgroundColor: '#8b5cf6', 
                              borderColor: '#8b5cf6', 
                              color: 'white',
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => handleSendToAmphora(order)}
                            disabled={isSendingToAmphora[`${order.order_id}-${order.sku}`]}
                          >
                            {isSendingToAmphora[`${order.order_id}-${order.sku}`] ? 'Enviando...' : 'Enviar a Amphora'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Tabla de BOM necesario */}
          {orders.length > 0 && (
            <div style={{ marginTop: '3rem' }}>
              <h3 style={{ marginBottom: '1.5rem' }}>BOM de Piezas Necesarias</h3>

              {loadingBOM ? (
                <div className="table-container">
                  <Skeleton height="200px" variant="rounded" />
                </div>
              ) : bomItems.length === 0 ? (
                <div className="empty-state">
                  <p>No se encontraron piezas necesarias o no hay BOM disponible para los pedidos.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cód. Pieza</th>
                        <th>Nombre Pieza</th>
                        <th style={{ textAlign: 'center' }}>Cantidad Necesaria</th>
                        <th style={{ textAlign: 'center' }}>Acabados Necesarios</th>
                        <th style={{ textAlign: 'center' }}>Stock Acabado</th>
                        <th style={{ textAlign: 'center' }}>Taller</th>
                        <th style={{ textAlign: 'center' }}>Pedir Fundición</th>
                        <th style={{ textAlign: 'center' }}>Cantidad Fundición</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomItems.map((item, index) => (
                        <tr key={`${item.cod_pieza}-${index}`}>
                          <td><strong>{item.cod_pieza}</strong></td>
                          <td>{item.nombre_pieza}</td>
                          <td style={{ textAlign: 'center' }}>
                            <strong>{item.cantidad_necesaria}</strong>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.acabados_necesarios && item.acabados_necesarios.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                                {item.acabados_necesarios.map((acabado, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      padding: '0.125rem 0.5rem',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      backgroundColor: acabado.acabado === 'PLATA' ? '#d1d5db' : acabado.acabado === 'PVD' ? '#fde047' : '#6366f1',
                                      color: acabado.acabado === 'PLATA' ? '#1f2937' : acabado.acabado === 'PVD' ? '#78350f' : '#ffffff',
                                    }}
                                    title={`${acabado.acabado}: ${acabado.cantidad} unidades`}
                                  >
                                    {acabado.acabado}: {acabado.cantidad}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.stock_acabado ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.stock_taller ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.pedir_fundicion ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.cantidad_fundicion > 0 ? (
                              <strong style={{ color: 'var(--error)' }}>
                                {item.cantidad_fundicion}
                              </strong>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      </div>
    </div>
  );
}

